import { assert } from '@deqo/foundation';
import { KeyInput } from './KeyInput';
import { Keys } from './Keys';
import { Modifiers } from './Modifiers';


/**
 * Represents a key expression; a pairing of a key and modifiers set.
 */
export class KeyExpression {

    public static readonly WildcardKey = -1;

    /**
     * Creates a KeyExpression object that matches the key and modifiers pressed in the the specified input.
     *
     * @param input the input to create the expression from
     */
    public static from(input: KeyExpression | KeyboardEvent | KeyInput): KeyExpression {
        if (input instanceof KeyExpression) return input;
        if (input instanceof KeyInput) return new KeyExpression(input.key as number, input.modifiers);
        return new KeyExpression(input.keyCode, Modifiers.fromEvent(input));
    }

    /**
     * Parses the specified key expression string and returns a KeyExpression object that represents the contents.
     *
     * @param input
     */
    public static parse(input: string): KeyExpression {
        assert(!!input, 'KeyExpression: input is invalid.');
        const keys = input.trim().split(/[\s+]/).map(x => Keys.parse(x, true));
        assert(!keys.find(x => !x), 'KeyExpression: input is invalid.');
        const key = keys.filter(x => x !== Keys.CTRL && x !== Keys.ALT && x !== Keys.SHIFT)[0] || null;
        assert(key !== null, 'KeyExpression: key must be specified.');
        return new KeyExpression(key, Modifiers.fromKeys(keys));
    }

    /**
     * The primary expression key.
     */
    public readonly key: number;

    /**
     * The modifier keys.
     */
    public readonly modifiers: Modifiers;

    private constructor(key: number, modifiers: Modifiers) {
        this.key = key;
        this.modifiers = modifiers;
    }

    /**
     * Tests whether the key expression matches the specified input (either event or another expression).  When
     * exact=true (default) the match includes false values for modifiers, otherwise only true values are considered.
     *
     * @param input
     * @param exact
     */
    public matches(input: KeyExpression | KeyboardEvent | KeyInput, exact = true): boolean {
        const other = KeyExpression.from(input);
        if (this.key !== KeyExpression.WildcardKey && this.key !== other.key) {
            return false;
        }
        if (!this.modifiers.matches(other.modifiers, exact)) {
            return false;
        }
        return true;
    }

    public toString(): string {
        const keys = [Keys.describe(this.key)];
        if (this.modifiers.ctrl) keys.push('CTRL');
        if (this.modifiers.alt) keys.push('ALT');
        if (this.modifiers.shift) keys.push('SHIFT');
        return keys.join('+');
    }
}
